import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { FormLabel } from '../../forms/components/FormLabel/FormLabel';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { catalogServiceCardCopy } from './catalogServiceCards';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectionProfileUrlModalProps {
  connection?: ServiceConnection;
  closeModal: () => void;
  onSave: (integrationType: string, artistProfileUrl: string | null) => Promise<boolean>;
  isLoading: boolean;
  error?: ErrorAPIResult['error'];
}

/**
 * The path that works without anybody's permission: paste the public artist
 * page. We read it only to list the releases, so claiming is one tap instead of
 * a search — it grants us nothing and costs the artist nothing.
 */
const ConnectionProfileUrlModalComponent: React.FC<ConnectionProfileUrlModalProps> = ({
  connection,
  closeModal,
  onSave,
  isLoading,
  error,
}) => {
  const intl = useIntl();
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(connection?.artistProfileUrl ?? '');
  }, [connection]);

  const onUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
  }, []);

  const integrationType = connection?.integrationType;
  const copy = integrationType === undefined ? undefined : catalogServiceCardCopy[integrationType];
  const serviceName =
    copy !== undefined
      ? intl.formatMessage(copy.programName)
      : ((integrationType !== undefined ? musicServicesByMusicAPIID[integrationType]?.name : undefined) ?? '');

  const onConfirm = useCallback(() => {
    if (integrationType === undefined) {
      return;
    }
    const trimmed = url.trim();
    onSave(integrationType, trimmed === '' ? null : trimmed)
      .then((saved) => {
        if (!saved) {
          return;
        }
        closeModal();
      })
      .catch(console.error);
  }, [closeModal, integrationType, onSave, url]);

  return (
    <Modal isOpen={connection !== undefined} toggle={closeModal} centered className="sml-modal">
      <ModalHeader toggle={closeModal}>
        {intl.formatMessage(
          { id: 'app.sml.catalog.profile_url.title', defaultMessage: 'Artist profile URL — {service}' },
          { service: serviceName }
        )}
      </ModalHeader>
      <ModalBody>
        {error !== undefined && <ErrorAlert error={error} />}
        <FormLabel>
          {intl.formatMessage({
            id: 'app.sml.catalog.profile_url.label',
            defaultMessage: 'Your public artist page',
          })}
        </FormLabel>
        <Input
          type="url"
          value={url}
          disabled={isLoading}
          onChange={onUrlChange}
          placeholder={intl.formatMessage({
            id: 'app.sml.catalog.profile_url.placeholder',
            defaultMessage: 'https://…',
          })}
        />
        <p className={styles.sheetNote}>
          {intl.formatMessage({
            id: 'app.sml.catalog.profile_url.help',
            defaultMessage:
              'Used only to read what the page shows anyone: your public releases, so claiming is one tap instead of a search, and your follower count once a day. Clear the field to remove it.',
          })}
        </p>
        <p className={styles.sheetNote}>
          {intl.formatMessage({
            id: 'app.sml.catalog.profile_url.bio_link_note',
            defaultMessage: 'Saving it also adds this profile to your bio link. Clearing it removes that block again.',
          })}
        </p>
      </ModalBody>
      <ModalFooter>
        <SMLButton outline onClick={closeModal} disabled={isLoading}>
          {intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
        </SMLButton>
        <SMLButton color="primary" onClick={onConfirm} disabled={isLoading}>
          {intl.formatMessage({ id: 'app.sml.catalog.profile_url.save', defaultMessage: 'Save URL' })}
        </SMLButton>
      </ModalFooter>
    </Modal>
  );
};

export const ConnectionProfileUrlModal = TypedMemo(ConnectionProfileUrlModalComponent);
